import {
  getLabelsLabelListUrl,
  getLabelsRenderUrl,
  getLabelsTemplatesQueryKey,
  labelsPreview,
  labelsSend,
  useLabelsCreateTemplate,
  useLabelsDeleteTemplate,
  useLabelsTemplates,
  useLabelsUpdateTemplate,
} from "@api/generated/labels/labels";
import type { LabelElement, LabelLayout, LabelsDataIn, LabelsDraft, LabelTemplate } from "@api/generated/model";
import { apiDownload } from "@api/http";
import { ONCE } from "@app/queryClient";
import { useTrial } from "@app/trials";
import { useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { ElementPanel, type FieldOption, PLOT_FIELDS } from "@features/documents/ElementPanel";
import { LabelCanvas, perPage, stockKey, stockSize } from "@features/documents/LabelCanvas";
import { SendToPrinter } from "@features/documents/SendToPrinter";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { PlotRange } from "@features/trials/PlotRange";
import { hint } from "@i18n";
import "@features/documents/labels.css";
import { parseLabelReplications } from "@features/documents/replications";
import { Button, Checkbox, Loader, Modal, MultiSelect, NumberInput, Select, TagsInput, Textarea, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChoiceList } from "@ui/ChoiceList";
import { formatNumber, num } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { SheetPicker, useSheetInfo } from "@ui/SheetPicker";
import { YearToggle } from "@ui/YearToggle";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const NEW: Record<LabelElement["type"], LabelElement> = {
  field: { type: "field", x: 3, y: 3, source: "trial_plot", size: 3 },
  barcode: { type: "barcode", x: 3, y: 10, source: "plot_key", width: 50, height: 9.5 },
  text: { type: "text", x: 3, y: 3, text: "Martonvásár", size: 2 },
  line: { type: "line", x: 2, y: 20, width: 40, height: 0.3 },
  command: { type: "command", x: 1, y: 1, text: "#PR4" },
};
const EMPTY: LabelsDraft = {
  template: null,
  layout: null,
  source: "trial",
  next_year: false,
  trials: [],
  sites: [],
  copies: 1,
  count: 1,
  random: "",
  serpentine: true,
  order: "walk",
  subplots: false,
  skip: 0,
};
const SOURCES = ["trial", "query", "sheet", "text"] as const;
// the built-in fields a saved query's row has when it has a plot
const QUERY_PLOT_FIELDS = PLOT_FIELDS.filter((f) => !["name", "pedigree", "source", "sor"].includes(f));

const PREVIEW_DELAY = 400;

/** What the labels are made of, as the API takes it (the random order parsed from its text). */
function dataOf({ random, ...rest }: PreviewRequest): LabelsDataIn {
  return { ...rest, reps: parseLabelReplications(random) };
}

export const Route = createFileRoute("/analysis/labels")({
  validateSearch: z.object({ ev: z.number().int().optional() }),
  component: LabelsPage,
});

/** Elemzés és nyomtatás → Címkék (the legacy frmNovexx / frmBarcode): a label template edited on a canvas (fields,
 *  a barcode, text, lines, Novexx commands), its data — trials' plots of the working season or the next one (for
 *  sowing; a plot range, the subplots, several trials), a saved query's rows (the legacy Query menu), an uploaded
 *  table's rows, or text-only labels —, optionally in a random order; printed as the Novexx printer's commands (.txt)
 *  or an A4 sticker PDF, listed as .xlsx. The work in progress is the user's draft (``labels_draft``). */
function LabelsPage() {
  const settings = useSettings();
  const templates = useLabelsTemplates().data;
  const first = templates?.[0]; // the built-in ones come first
  if (!settings || !templates || !first) return <Loader size="sm" m="md" />;
  return <Labels stored={settings.labels_draft ?? null} templates={templates} first={first} />;
}

/** The request of a preview as text: its query key, debounced (the random order may be a long pasted text). */
type PreviewRequest = Omit<LabelsDataIn, "reps"> & { random: string };

function Labels({ stored, templates, first }: { stored: LabelsDraft | null; templates: LabelTemplate[]; first: LabelTemplate }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const season = useSeasonFromLink(Route.useSearch().ev);
  const [draft, setDraft] = useState<LabelsDraft>(stored?.layout ? stored : { ...EMPTY, template: first.name, layout: first.layout });
  useAutosave("labels_draft", draft, stored);
  const change = (p: Partial<LabelsDraft>) => setDraft((d) => ({ ...d, ...p }));
  const layout = draft.layout ?? first.layout;
  const setLayout = (l: LabelLayout) => change({ layout: l });
  const [selected, setSelected] = useState<number | null>(null);
  const [index, setIndex] = useState(0);
  const [naming, setNaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const source = draft.source ?? "trial";
  const ev = season != null ? season + (draft.next_year ? 1 : 0) : undefined;
  const { trials } = useTrial(ev, undefined, undefined);
  const sites = draft.sites?.length ? draft.sites : ["MV"];
  const sheetInfo = useSheetInfo(source === "sheet" ? draft.sheet : null);
  const saved = useSavedQuery(draft.query, source === "query");
  const copies = draft.copies ?? 1;
  const random = draft.random ?? "";
  const ordered = { random, serpentine: draft.serpentine ?? true, order: draft.order ?? "walk" };
  const chosenTrials = draft.trials ?? [];
  const requests: Record<(typeof SOURCES)[number], PreviewRequest | null> = {
    sheet: draft.sheet ? { sheet: draft.sheet, copies, random: "" } : null,
    query: draft.query
      ? { query: draft.query, sites: draft.sites ?? [], replace_sites: !!draft.replace_sites && !!draft.sites?.length, copies, ...ordered }
      : null,
    text: { count: draft.count ?? 1, copies, random: "" },
    trial:
      ev && chosenTrials.length
        ? {
            ev,
            trials: chosenTrials,
            sites,
            copies,
            ...ordered,
            parc_from: draft.parc_from ?? null,
            parc_to: draft.parc_to ?? null,
            subplots: draft.subplots ?? false,
          }
        : null,
  };
  const request = requests[source];
  // the labels depend on the data, not on the design: the preview is read again only when the data settles (a saved
  // query's rows also when it was saved again in the query builder)
  const [settled] = useDebouncedValue(request ? JSON.stringify(request) : "", PREVIEW_DELAY);
  const preview = useQuery({
    queryKey: ["labels-preview", settled, saved.stamp],
    queryFn: () => labelsPreview(dataOf(JSON.parse(settled) as PreviewRequest)),
    enabled: !!settled && saved.ready,
    ...ONCE,
  });
  useEffect(() => {
    if (preview.error) notifyError(preview.error);
  }, [preview.error]);
  const total = preview.data?.total ?? 0;
  const shown = preview.data?.labels ?? [];
  const at = Math.max(0, Math.min(index, shown.length - 1));
  const fields: FieldOption[] | undefined =
    source === "sheet"
      ? // the preview's columns: a header naming a built-in field (a label list uploaded again) is that field
        (preview.data?.columns?.map((c) => ({ value: c.key, label: c.name })) ??
        (sheetInfo.data?.columns ?? []).map((c) => ({ value: c, label: c })))
      : source === "query"
        ? [
            ...(preview.data?.columns ?? []).map((c) => ({ value: c.key, label: c.name })),
            ...QUERY_PLOT_FIELDS.map((f) => ({ value: f, label: t(`labels.field.${f}`) })),
          ]
        : undefined;
  const template = templates.find((x): x is LabelTemplate & { id: number } => x.name === draft.template && x.id != null);
  const refresh = () => void client.invalidateQueries({ queryKey: getLabelsTemplatesQueryKey() });
  // the layout differs from the saved template it came from
  const dirty = !!template && JSON.stringify(template.layout) !== JSON.stringify(layout);
  const [switching, setSwitching] = useState<string | null>(null); // a template to open over unsaved changes
  const [deleting, setDeleting] = useState(false);
  const openTemplate = (name: string) => {
    const x = templates.find((y) => y.name === name);
    if (x) change({ template: x.name, layout: x.layout });
    setSelected(null);
  };
  const create = useLabelsCreateTemplate({
    mutation: {
      onSuccess: (x) => {
        change({ template: x.name, layout: x.layout }); // as stored (with the server's defaults): not "modified"
        setNaming(null);
        refresh();
        notifications.show({ color: "green", message: t("labels.saved", { name: x.name }) });
      },
    },
  });
  const update = useLabelsUpdateTemplate({
    mutation: {
      onSuccess: (x) => {
        change({ layout: x.layout });
        refresh();
        notifications.show({ color: "green", message: t("labels.saved", { name: x.name }) });
      },
    },
  });
  const remove = useLabelsDeleteTemplate({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("labels.deleted", { name: template?.name ?? "" }) });
        setDeleting(false);
        change({ template: null });
        refresh();
      },
    },
  });
  const setElement = (i: number, e: LabelElement) =>
    setLayout({ ...layout, elements: (layout.elements ?? []).map((x, j) => (j === i ? e : x)) });
  const add = (type: LabelElement["type"]) => {
    const elements = [...(layout.elements ?? []), NEW[type]];
    setLayout({ ...layout, elements });
    setSelected(elements.length - 1);
  };
  const removeElement = (i: number) => {
    setLayout({ ...layout, elements: (layout.elements ?? []).filter((_, j) => j !== i) });
    setSelected(null);
  };
  const download = (url: string, body: object, file: string, done: string) => {
    setBusy(true);
    const init = { method: "POST", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } };
    apiDownload(url, init, file)
      .then(() => notifications.show({ color: "green", message: t(done, { file }) }))
      .catch(notifyError)
      .finally(() => setBusy(false));
  };
  const novexx = layout.stock.kind === "novexx";
  // only labels first … last of the run (numbered as printed, the copies counted): what a jammed printer spoilt
  const fromLabel = draft.first ?? null;
  const toLabel = draft.last ?? null;
  const printing = total ? Math.max(0, Math.min(toLabel ?? total, total) - (fromLabel ?? 1) + 1) : 0;
  const printBody = (test: boolean) =>
    request ? { ...dataOf(request), layout, skip: novexx ? 0 : (draft.skip ?? 0), test, first: fromLabel, last: toLabel } : null;
  const print = (test: boolean) => {
    const body = printBody(test);
    if (!body) return;
    download(
      getLabelsRenderUrl(),
      body,
      novexx ? t("labels.fileNovexx") : t("labels.fileA4"),
      novexx ? "labels.downloadedNovexx" : "labels.downloadedA4",
    );
  };
  const [w, h] = stockSize(layout);
  const reps = source === "trial" || source === "query";
  return (
    <div className="page fill">
      <PageHeader
        title={t("labels.title")}
        subtitle={t("labels.subtitle")}
        season
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            {dirty && <span className="chip y">{t("labels.modified")}</span>}
            {template && (
              <Button
                size="compact-sm"
                variant={dirty ? "filled" : "default"}
                disabled={!dirty}
                loading={update.isPending}
                onClick={() => update.mutate({ id: template.id, data: { name: template.name, layout } })}
              >
                {t("labels.save")}
              </Button>
            )}
            <Button
              size="compact-sm"
              variant="default"
              onClick={() => setNaming(draft.template ? t("labels.copyName", { name: draft.template }) : "")}
            >
              {t("labels.saveAsTemplate")}
            </Button>
          </div>
        }
      />
      <div className="fill-row" style={{ alignItems: "flex-start" }}>
        <ChoiceList
          title={t("labels.templates")}
          action={
            template && (
              <Button size="compact-xs" variant="subtle" color="red" onClick={() => setDeleting(true)}>
                {t("labels.delete")}
              </Button>
            )
          }
          items={templates.map((x) => ({ id: x.name, title: x.name, sub: t(`labels.stock.${stockKey(x.layout.stock)}`) }))}
          value={draft.template ?? null}
          onChange={(name) => (dirty ? setSwitching(String(name)) : openTemplate(String(name)))}
        />
        <section className="card pad stack grow" style={{ gap: 10, minWidth: 0 }}>
          <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
            <b className="grow">{`${draft.template ?? t("labels.newTemplate")} · ${formatNumber(w, 1)} × ${formatNumber(h, 1)} mm`}</b>
            {(["field", "barcode", "text", "line", ...(novexx ? (["command"] as const) : [])] as const).map((type) => (
              <button key={type} type="button" className="chip" onClick={() => add(type)}>
                + {t(`labels.elementType.${type}`)}
              </button>
            ))}
          </div>
          <div className="soft lb-stage">
            <LabelCanvas
              layout={layout}
              label={shown[at]}
              selected={selected}
              onSelect={setSelected}
              onChange={setElement}
              onDelete={removeElement}
            />
          </div>
          <div className="hstack" style={{ gap: 8 }}>
            <span className="sm mu grow">{t("labels.keyboardHelp")}</span>
            <button type="button" className="chip" disabled={at <= 0} onClick={() => setIndex(at - 1)} aria-label={t("labels.previous")}>
              ‹
            </button>
            <span className="sm">{total ? t("labels.preview", { n: at + 1, count: total }) : t("labels.noData")}</span>
            <button
              type="button"
              className="chip"
              disabled={at >= shown.length - 1}
              onClick={() => setIndex(at + 1)}
              aria-label={t("labels.next")}
            >
              ›
            </button>
          </div>
          <ElementPanel
            fields={fields}
            layout={layout}
            selected={selected}
            onElement={(e) => selected != null && setElement(selected, e)}
            onLayout={setLayout}
          />
        </section>
        <section className="card pad stack lb-run" style={{ gap: 10 }}>
          <span className="sec">{t("labels.data")}</span>
          <Select
            size="xs"
            label={t("labels.sourceTitle")}
            value={source}
            allowDeselect={false}
            onChange={(v) => change({ source: (v ?? "trial") as LabelsDraft["source"] })}
            data={SOURCES.map((s) => ({ value: s, label: t(`labels.source.${s}`) }))}
          />
          {source === "sheet" && <SheetPicker value={draft.sheet} onChange={(sheet) => change({ sheet })} />}
          {source === "query" && (
            <>
              <SavedQuerySelect value={draft.query ?? null} onChange={(id) => change({ query: id })} required />
              <p className="sm mu">{t("labels.queryHelp")}</p>
            </>
          )}
          {source === "text" && (
            <NumberInput
              size="xs"
              label={t("labels.count")}
              description={t("labels.textOnlyHelp")}
              allowDecimal={false}
              min={1}
              max={20_000}
              value={draft.count ?? 1}
              onChange={(v) => change({ count: num(v) ?? 1 })}
            />
          )}
          {source === "trial" && (
            <>
              <YearToggle season={season} next={!!draft.next_year} onChange={(next_year) => change({ next_year, trials: [] })} />
              <MultiSelect
                placeholder={chosenTrials.length ? undefined : hint("searchSelect")}
                size="xs"
                searchable
                label={t("labels.trials")}
                description={chosenTrials.length > 1 ? t("labels.trialsHelp") : undefined}
                value={chosenTrials}
                onChange={(v) => change({ trials: v })}
                data={trials.map((x) => x.kis)}
              />
            </>
          )}
          {reps && (
            <TagsInput
              size="xs"
              label={t("labels.sites")}
              description={source === "query" ? t("labels.querySiteHelp") : undefined}
              value={source === "query" ? (draft.sites ?? []) : sites}
              onChange={(v) => change({ sites: v.map((s) => s.toUpperCase()) })}
            />
          )}
          {source === "query" && (
            <Checkbox
              size="xs"
              label={t("labels.replaceSites")}
              description={t("labels.replaceSitesHelp")}
              disabled={!draft.sites?.length}
              checked={!!draft.replace_sites}
              onChange={(ev) => change({ replace_sites: ev.currentTarget.checked })}
            />
          )}
          {source === "trial" && (
            <>
              <div className="hstack" style={{ gap: 8 }}>
                <PlotRange
                  size="xs"
                  from={draft.parc_from ?? ""}
                  to={draft.parc_to ?? ""}
                  onFrom={(v) => change({ parc_from: num(v) })}
                  onTo={(v) => change({ parc_to: num(v) })}
                  fromLabel={t("labels.plotFrom")}
                  toLabel={t("labels.plotTo")}
                />
              </div>
              <Checkbox
                size="xs"
                label={t("labels.subplots")}
                checked={!!draft.subplots}
                onChange={(ev) => change({ subplots: ev.currentTarget.checked })}
              />
            </>
          )}
          <div className="hstack" style={{ gap: 8 }}>
            <NumberInput
              size="xs"
              w={90}
              label={t("labels.copies")}
              min={1}
              max={10}
              value={copies}
              onChange={(v) => change({ copies: num(v) ?? 1 })}
            />
            {!novexx && (
              <NumberInput
                size="xs"
                w={110}
                label={t("labels.alreadyUsed")}
                min={0}
                max={perPage(layout.stock) - 1}
                value={draft.skip ?? 0}
                onChange={(v) => change({ skip: num(v) ?? 0 })}
              />
            )}
          </div>
          {reps && (
            <Textarea
              size="xs"
              autosize
              minRows={2}
              maxRows={6}
              label={t("labels.randomOrder")}
              description={t("labels.randomOrderHelp")}
              value={random}
              onChange={(ev) => change({ random: ev.currentTarget.value })}
            />
          )}
          {reps && !!random.trim() && (
            <>
              <Select
                size="xs"
                label={t("labels.order")}
                value={draft.order ?? "walk"}
                allowDeselect={false}
                onChange={(v) => change({ order: (v ?? "walk") as LabelsDraft["order"] })}
                data={(["walk", "plot"] as const).map((o) => ({ value: o, label: t(`labels.orders.${o}`) }))}
              />
              {(draft.order ?? "walk") === "walk" && (
                <Checkbox
                  size="xs"
                  label={t("labels.serpentine")}
                  checked={draft.serpentine ?? true}
                  onChange={(ev) => change({ serpentine: ev.currentTarget.checked })}
                />
              )}
            </>
          )}
          <div className="hstack" style={{ gap: 8 }}>
            <NumberInput
              size="xs"
              label={t("labels.printFrom")}
              allowDecimal={false}
              min={1}
              placeholder="1"
              value={fromLabel ?? ""}
              onChange={(v) => change({ first: num(v) })}
            />
            <NumberInput
              size="xs"
              label={t("labels.printTo")}
              allowDecimal={false}
              min={1}
              placeholder={total ? String(total) : undefined}
              value={toLabel ?? ""}
              onChange={(v) => change({ last: num(v) })}
            />
          </div>
          <span className="sm mu">{t("labels.printRangeHelp")}</span>
          <Button variant="default" disabled={!printing} loading={busy} onClick={() => print(true)}>
            {t("labels.testLabel")}
          </Button>
          <Button disabled={!printing} loading={busy} onClick={() => print(false)}>
            {novexx ? t("labels.downloadNovexx", { count: printing }) : t("labels.print", { count: printing })}
          </Button>
          {novexx && (
            <SendToPrinter
              disabled={!printing}
              printer={draft.printer}
              onPrinter={(printer) => change({ printer })}
              send={(printer) => {
                const body = printBody(false);
                return body ? labelsSend({ ...body, printer }) : Promise.reject(new Error("no labels"));
              }}
            />
          )}
          {source !== "text" && (
            <Button
              variant="subtle"
              size="compact-sm"
              disabled={!total || !request}
              loading={busy}
              onClick={() => request && download(getLabelsLabelListUrl(), dataOf(request), t("labels.fileList"), "labels.listDownloaded")}
            >
              {t("labels.list")}
            </Button>
          )}
          <p className="sm mu">{novexx ? t("labels.novexxHelp") : t("labels.a4Help")}</p>
        </section>
      </div>
      {naming != null && (
        <Modal opened onClose={() => setNaming(null)} title={t("labels.saveAsTemplate")}>
          <div className="stack" style={{ gap: 10 }}>
            <TextInput
              label={t("labels.templateName")}
              value={naming}
              onChange={(ev) => setNaming(ev.currentTarget.value)}
              data-autofocus
            />
            <Button
              disabled={!naming.trim()}
              loading={create.isPending}
              onClick={() => create.mutate({ data: { name: naming.trim(), layout } })}
            >
              {t("labels.save")}
            </Button>
          </div>
        </Modal>
      )}
      {deleting && template && (
        <ConfirmModal
          title={t("labels.deleteTitle")}
          text={t("labels.deleteConfirm", { name: template.name })}
          confirm={t("labels.delete")}
          danger
          loading={remove.isPending}
          onConfirm={() => remove.mutate({ id: template.id })}
          onClose={() => setDeleting(false)}
        />
      )}
      {switching != null && (
        <ConfirmModal
          title={t("labels.discardTitle")}
          text={t("labels.discardConfirm", { name: template?.name ?? "" })}
          confirm={t("labels.discardAndOpen")}
          danger
          onConfirm={() => {
            openTemplate(switching);
            setSwitching(null);
          }}
          onClose={() => setSwitching(null)}
        />
      )}
    </div>
  );
}
